// src/components/Invoices/InvoiceForm.jsx
import React, { useState, useEffect } from 'react';
import { transactionService } from '../../services/transactions';
import { customerService, productService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { ShoppingBagIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

export default function InvoiceForm({ invoice, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    customer_id: invoice?.customer_id || '',
    items: invoice?.items || [{ product_id: '', quantity: 1, unit_price: 0, product_name: '', product_type: 'product' }],
    due_date: invoice?.due_date || '',
    notes: invoice?.notes || '',
  });
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Load customers and products
  useEffect(() => {
    loadCustomers();
    loadProducts();
  }, []);

  // Filter products based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const response = await customerService.getCustomers({ limit: 100 });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await productService.getProducts({ limit: 100 });
      setProducts(response.data || []);
      setFilteredProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one item
    const validItems = formData.items.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item to the invoice');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        customer_id: formData.customer_id || null,
        items: validItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          description: item.product_name,
          tax_rate: item.tax_rate || 16
        })),
        due_date: formData.due_date || null,
        notes: formData.notes || null
      };
      
      console.log('📤 Submitting invoice:', submitData);
      
      if (invoice) {
        await transactionService.updateTransaction(invoice.id, submitData);
        toast.success('Invoice updated successfully');
      } else {
        await transactionService.createInvoice(submitData);
        toast.success('Invoice created successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Invoice error:', error);
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: 1, unit_price: 0, product_name: '', product_type: 'product' }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const selectProduct = (index, product) => {
    const newItems = [...formData.items];
    newItems[index] = {
      product_id: product.id,
      product_name: product.name,
      product_type: product.product_type || 'product',
      unit_type: product.unit_type || 'piece',
      quantity: 1,
      unit_price: Number(product.unit_price) || 0,
      tax_rate: Number(product.tax_rate) || 16
    };
    setFormData({ ...formData, items: newItems });
    setSearchTerm('');
  };

  const updateQuantity = (index, quantity) => {
    const newItems = [...formData.items];
    newItems[index].quantity = Math.max(1, parseInt(quantity) || 1);
    setFormData({ ...formData, items: newItems });
  };

  const updateUnitPrice = (index, unitPrice) => {
    const newItems = [...formData.items];
    newItems[index].unit_price = Math.max(0, parseFloat(unitPrice) || 0);
    setFormData({ ...formData, items: newItems });
  };

  // Get unit type label
  const getUnitTypeLabel = (unitType) => {
    switch(unitType) {
      case 'hour': return 'per hour';
      case 'day': return 'per day';
      case 'month': return 'per month';
      default: return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Customer Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Customer (Optional)
        </label>
        <select
          value={formData.customer_id}
          onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loadingCustomers}
        >
          <option value="">-- Select Customer (Optional) --</option>
          {customers.map(customer => (
            <option key={customer.id} value={customer.id}>
              {customer.name} {customer.phone ? `- ${customer.phone}` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Leave empty for walk-in customers
        </p>
      </div>

      {/* Due Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Due Date (Optional)
        </label>
        <input
          type="date"
          value={formData.due_date}
          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Invoice Items */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Invoice Items</label>
          <button
            type="button"
            onClick={addItem}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Add Item
          </button>
        </div>
        
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {formData.items.map((item, index) => (
            <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
              {/* Product/Search Selection */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Item {index + 1}
                </label>
                {!item.product_id ? (
                  <div>
                    <input
                      type="text"
                      placeholder="Search product or service by name or SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      disabled={loadingProducts}
                    />
                    {searchTerm && filteredProducts.length > 0 && (
                      <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white">
                        {filteredProducts.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => selectProduct(index, product)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b last:border-b-0"
                          >
                            <div className="flex items-center space-x-2">
                              {product.product_type === 'service' ? (
                                <WrenchScrewdriverIcon className="h-4 w-4 text-purple-500" />
                              ) : (
                                <ShoppingBagIcon className="h-4 w-4 text-blue-500" />
                              )}
                              <div className="font-medium">{product.name}</div>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                product.product_type === 'service' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {product.product_type === 'service' ? 'Service' : 'Product'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              SKU: {product.sku} | Price: KES {product.unit_price}
                              {product.product_type === 'service' && product.unit_type !== 'piece' && (
                                <span className="ml-1 text-purple-600">({getUnitTypeLabel(product.unit_type)})</span>
                              )}
                              {product.product_type === 'product' && (
                                <span className="ml-1">| Stock: {product.current_stock}</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchTerm && filteredProducts.length === 0 && !loadingProducts && (
                      <p className="text-xs text-gray-500 mt-1">No items found</p>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-between items-center p-2 rounded-lg" style={{
                    backgroundColor: item.product_type === 'service' ? '#f3e8ff' : '#eff6ff'
                  }}>
                    <div className="flex items-center space-x-2">
                      {item.product_type === 'service' ? (
                        <WrenchScrewdriverIcon className="h-5 w-5 text-purple-600" />
                      ) : (
                        <ShoppingBagIcon className="h-5 w-5 text-blue-600" />
                      )}
                      <div>
                        <div className="font-medium">{item.product_name}</div>
                        <div className="text-xs text-gray-600">
                          {item.product_type === 'service' ? 'Service' : 'Product'} | 
                          Price: KES {item.unit_price}
                          {item.product_type === 'service' && item.unit_type !== 'piece' && (
                            <span className="ml-1 text-purple-600">({getUnitTypeLabel(item.unit_type)})</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = [...formData.items];
                        newItems[index] = { product_id: '', quantity: 1, unit_price: 0, product_name: '', product_type: 'product' };
                        setFormData({ ...formData, items: newItems });
                      }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>
              
              {/* Quantity and Price */}
              {item.product_id && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(index, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    {item.product_type === 'service' && item.unit_type !== 'piece' && (
                      <p className="text-xs text-gray-400 mt-0.5">{getUnitTypeLabel(item.unit_type)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Unit Price (KES)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateUnitPrice(index, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>
              )}
              
              {/* Remove button */}
              {formData.items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="mt-2 text-xs text-red-600 hover:text-red-700"
                >
                  Remove item
                </button>
              )}
            </div>
          ))}
        </div>
        
        {formData.items.length === 0 && (
          <p className="text-center text-gray-500 py-4">No items added. Click "Add Item" to continue.</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (Optional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows="3"
          placeholder="Additional notes..."
        />
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-4">
        <button 
          type="submit" 
          disabled={loading} 
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            invoice ? 'Update Invoice' : 'Create Invoice'
          )}
        </button>
        <button 
          type="button" 
          onClick={onCancel} 
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}